import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const REMINDER_TOAST_DURATION_MS = 10 * 1000; // 10 seconds

export type ReminderToastContent = { title: string; description: React.ReactNode };

export type ShowReminderOptions = { onToastClick?: () => void };

type ReminderToastContextValue = {
  showReminderAtTop: (title: string, description: React.ReactNode, options?: ShowReminderOptions) => void;
  clearToast: () => void;
};

const ReminderToastContext = createContext<ReminderToastContextValue | null>(null);

export function ReminderToastProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [toast, setToast] = useState<ReminderToastContent | null>(null);
  const reminderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastShownAtRef = useRef<number | null>(null);
  const toastVisibleRef = useRef<boolean>(false);
  const onToastClickRef = useRef<(() => void) | null>(null);

  const clearToast = useCallback(() => {
    if (reminderTimeoutRef.current) {
      clearTimeout(reminderTimeoutRef.current);
      reminderTimeoutRef.current = null;
    }
    onToastClickRef.current = null;
    setToast(null);
    toastVisibleRef.current = false;
    toastShownAtRef.current = null;
  }, []);

  const handleToastClick = useCallback(() => {
    const fn = onToastClickRef.current;
    clearToast();
    fn?.();
  }, [clearToast]);

  const showReminderAtTop = useCallback(
    (title: string, description: React.ReactNode, options?: ShowReminderOptions) => {
      if (reminderTimeoutRef.current) {
        clearTimeout(reminderTimeoutRef.current);
      }
      onToastClickRef.current = options?.onToastClick ?? null;
      setToast({ title, description });
      toastShownAtRef.current = Date.now();
      toastVisibleRef.current = true;
      reminderTimeoutRef.current = setTimeout(() => {
        clearToast();
      }, REMINDER_TOAST_DURATION_MS);
    },
    [clearToast]
  );

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (toastVisibleRef.current && toastShownAtRef.current) {
          const elapsed = Date.now() - toastShownAtRef.current;
          if (elapsed >= REMINDER_TOAST_DURATION_MS) {
            clearToast();
          } else {
            if (reminderTimeoutRef.current) clearTimeout(reminderTimeoutRef.current);
            reminderTimeoutRef.current = setTimeout(() => {
              clearToast();
            }, REMINDER_TOAST_DURATION_MS - elapsed);
          }
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (reminderTimeoutRef.current) clearTimeout(reminderTimeoutRef.current);
    };
  }, [clearToast]);

  const value: ReminderToastContextValue = { showReminderAtTop, clearToast };

  const toastUI =
    toast &&
    createPortal(
      <div
        className="fixed top-4 right-16 z-[9999] flex w-[320px] max-w-[calc(100vw-4rem)] flex-col gap-1 rounded-md border border-border bg-background p-4 pr-8 shadow-lg animate-in slide-in-from-right-2"
        role="status"
      >
        <button
          type="button"
          className="absolute right-2 top-2 rounded-md p-1 text-foreground/50 hover:text-foreground z-10"
          onClick={(e) => {
            e.stopPropagation();
            clearToast();
          }}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <button
          type="button"
          className="w-full text-left rounded-md cursor-pointer py-0.5 -m-0.5"
          onClick={handleToastClick}
          aria-label="Open notifications"
        >
          <p className="text-sm font-semibold pr-6">{toast.title}</p>
          {toast.description != null && (
            <div className="text-sm opacity-90">{toast.description}</div>
          )}
        </button>
      </div>,
      document.body
    );

  return (
    <ReminderToastContext.Provider value={value}>
      {children}
      {toastUI}
    </ReminderToastContext.Provider>
  );
}

export function useReminderToast(): ReminderToastContextValue {
  const ctx = useContext(ReminderToastContext);
  if (!ctx) {
    return {
      showReminderAtTop: () => {},
      clearToast: () => {},
    };
  }
  return ctx;
}
